import { clamp, toFinite } from '../math/MathUtils';
import { type Vector2 } from '../math/Vector2';
import { type Camera } from '../rendering/Camera';
import { type Viewport } from '../rendering/Viewport';
import {
  isRelativePointerSample,
  KeyPhase,
  PointerPhase,
  PointerState,
  type KeySample,
  type PointerSample,
} from './PointerState';

const DEFAULT_SENSITIVITY = 1;
const HALF = 0.5;

/**
 * The state of the pointer and the keyboard, sampled once per frame.
 *
 * Raw events arrive whenever the browser feels like it, several per frame or none for a second. They
 * are queued here and drained at one fixed point in the frame, so every behavior in a frame reads the
 * same pointer position and "pressed this frame" means the same thing to all of them. Reading live DOM
 * state instead would let two behaviors in one frame disagree.
 *
 * There is no DOM anywhere in this class. Events are pushed in through {@link InputManager.submitPointer}
 * and {@link InputManager.submitKey}, which is what `DomInputBinding` does in a browser and what a test
 * does directly.
 *
 * Several fingers are tracked well enough to answer the question a game actually asks: the pointer is
 * down while any of them is down, and the position follows the first one that landed. A game that
 * needs per-finger detail is a reason to grow this, not a reason to guess now.
 *
 * Samples arrive in one of two shapes and this is where the difference ends. An absolute sample says
 * where the pointer is; a relative one, which is all a locked pointer produces, says how far it moved,
 * and the position is accumulated here. Both land in the same {@link PointerState}, so no behavior,
 * system or game ever asks which mode it is in.
 */
export class InputManager {
  readonly #pointer = new PointerState();
  readonly #activePointers = new Set<number>();
  readonly #pointerQueue: PointerSample[] = [];
  readonly #keyQueue: KeySample[] = [];
  readonly #keysDown = new Set<string>();
  readonly #keysPressed = new Set<string>();
  readonly #keysReleased = new Set<string>();

  #primaryPointerId: number | undefined;
  #pointerSensitivity = DEFAULT_SENSITIVITY;
  #hasPointerPosition = false;

  /** Pointer position and press state for the current frame. Treat it as read only. */
  get pointer(): PointerState {
    return this.#pointer;
  }

  /**
   * How far the pointer travels per unit of movement while the pointer is locked.
   *
   * A plain multiplier where one matches the speed the operating system's own cursor would have moved,
   * so a half makes the pointer half as fast. It is applied in CSS pixels before anything reaches world
   * units, which keeps the feel the same whatever the camera is doing, and it does nothing at all while
   * the pointer is unlocked: an absolute sample already says where the pointer is.
   *
   * With the acceleration curve left on, which is the default, this scales an already non-linear signal.
   * A half is "about half as fast", not half the physical distance.
   */
  get pointerSensitivity(): number {
    return this.#pointerSensitivity;
  }

  set pointerSensitivity(value: number) {
    this.#pointerSensitivity = Math.max(0, toFinite(value, DEFAULT_SENSITIVITY));
  }

  /** Number of fingers, pens or buttons currently down. */
  get activePointerCount(): number {
    return this.#activePointers.size;
  }

  /** Whether a physical key is currently held. */
  isKeyDown(code: string): boolean {
    return this.#keysDown.has(code);
  }

  /** Whether a key went down during this frame. */
  wasKeyPressed(code: string): boolean {
    return this.#keysPressed.has(code);
  }

  /** Whether a key came up during this frame. */
  wasKeyReleased(code: string): boolean {
    return this.#keysReleased.has(code);
  }

  /** Queues a pointer event. Called by whatever is listening to the page, never by gameplay. */
  submitPointer(sample: PointerSample): void {
    this.#pointerQueue.push(sample);
  }

  /** Queues a keyboard event. Called by whatever is listening to the page, never by gameplay. */
  submitKey(sample: KeySample): void {
    this.#keyQueue.push(sample);
  }

  /**
   * Queues a cancel for every pointer currently down, without moving the pointer.
   *
   * What a lost pointer lock needs: the escape key that ends a lock does not release the mouse button,
   * so no release ever arrives and a press stays held forever. Going through the queue rather than
   * clearing the state by hand means the cancel is drained like any other event, so a drag in progress
   * ends properly and the entity holding it hears about it.
   */
  cancelPointers(): void {
    for (const pointerId of this.#activePointers) {
      this.#pointerQueue.push({ pointerId, phase: PointerPhase.Cancel, dx: 0, dy: 0 });
    }
  }

  /**
   * Drains both queues into the frame's state and converts the pointer into world units.
   *
   * @internal Called by `InputSystem` at the start of the frame; gameplay reads the result instead.
   */
  beginFrame(camera: Camera): void {
    this.#pointer.pressedThisFrame = false;
    this.#pointer.releasedThisFrame = false;
    this.#keysPressed.clear();
    this.#keysReleased.clear();

    for (const sample of this.#pointerQueue) {
      this.#applyPointer(sample, camera.viewport);
    }
    this.#pointerQueue.length = 0;

    for (const sample of this.#keyQueue) {
      this.#applyKey(sample);
    }
    this.#keyQueue.length = 0;

    this.#toWorld(camera, this.#pointer.screenPosition, this.#pointer.worldPosition);
    if (this.#pointer.pressedThisFrame) {
      this.#toWorld(camera, this.#pointer.pressPosition, this.#pointer.pressWorldPosition);
    }
    if (this.#pointer.releasedThisFrame) {
      this.#toWorld(camera, this.#pointer.releasePosition, this.#pointer.releaseWorldPosition);
    }
  }

  /** Drops every queued event and every held state, as a scene change or a lost focus should. */
  reset(): void {
    this.#pointerQueue.length = 0;
    this.#keyQueue.length = 0;
    this.#activePointers.clear();
    this.#keysDown.clear();
    this.#keysPressed.clear();
    this.#keysReleased.clear();
    this.#primaryPointerId = undefined;

    this.#pointer.isDown = false;
    this.#pointer.pressedThisFrame = false;
    this.#pointer.releasedThisFrame = false;
    this.#pointer.isOverCanvas = false;
  }

  #applyPointer(sample: PointerSample, viewport: Viewport): void {
    switch (sample.phase) {
      case PointerPhase.Down:
        this.#trackPosition(sample, viewport, this.#activePointers.size === 0);
        if (this.#activePointers.size === 0) {
          this.#pointer.pressedThisFrame = true;
          this.#positionOf(sample, this.#pointer.pressPosition);
          this.#primaryPointerId = sample.pointerId;
        }
        this.#activePointers.add(sample.pointerId);
        this.#pointer.isDown = true;
        this.#pointer.isOverCanvas = true;
        return;

      case PointerPhase.Move:
        this.#trackPosition(sample, viewport, false);
        this.#pointer.isOverCanvas = true;
        return;

      case PointerPhase.Up:
      case PointerPhase.Cancel:
        this.#trackPosition(sample, viewport, false);
        this.#releasePointer(sample);
        return;

      case PointerPhase.Leave:
        this.#pointer.isOverCanvas = false;
        this.#releasePointer(sample);
        return;

      default:
        return;
    }
  }

  /*
   * The first press takes the position with it, so a tap that arrives with no preceding move still
   * puts the pointer where the finger landed. Otherwise only the finger being followed moves it, and
   * while nothing is down any pointer may, which is what makes mouse hover work. A second finger
   * landing is none of those cases: it must not drag the position off the finger being followed.
   */
  #trackPosition(sample: PointerSample, viewport: Viewport, isFirstPress: boolean): void {
    const isPrimary = this.#primaryPointerId === undefined || this.#primaryPointerId === sample.pointerId;
    if (!isFirstPress && !isPrimary) {
      return;
    }

    if (isRelativePointerSample(sample)) {
      this.#accumulate(sample.dx, sample.dy, viewport);
      return;
    }
    this.#pointer.screenPosition.set(sample.x, sample.y);
    this.#hasPointerPosition = true;
  }

  /*
   * Clamped to the viewport rather than to the whole canvas: the letterbox bars are outside the
   * playfield, and a cursor the player cannot see driven into a bar with nothing to point at helps
   * nobody. Clamping on every step also absorbs a resize mid-lock, which leaves the running total
   * outside the new bounds until the next movement pulls it back in.
   */
  #accumulate(dx: number, dy: number, viewport: Viewport): void {
    const position = this.#pointer.screenPosition;
    if (!this.#hasPointerPosition) {
      position.set(viewport.offsetX + viewport.width * HALF, viewport.offsetY + viewport.height * HALF);
      this.#hasPointerPosition = true;
    }

    const sensitivity = this.#pointerSensitivity;
    position.set(
      clamp(position.x + dx * sensitivity, viewport.offsetX, viewport.offsetX + viewport.width),
      clamp(position.y + dy * sensitivity, viewport.offsetY, viewport.offsetY + viewport.height),
    );
  }

  /** Where a sample happened: its own coordinates, or the accumulated position when it carries none. */
  #positionOf(sample: PointerSample, out: Vector2): void {
    if (isRelativePointerSample(sample)) {
      out.copyFrom(this.#pointer.screenPosition);
      return;
    }
    out.set(sample.x, sample.y);
  }

  #releasePointer(sample: PointerSample): void {
    if (!this.#activePointers.delete(sample.pointerId)) {
      return;
    }
    if (this.#primaryPointerId === sample.pointerId) {
      this.#primaryPointerId = [...this.#activePointers][0];
    }
    if (this.#activePointers.size === 0) {
      this.#pointer.isDown = false;
      this.#pointer.releasedThisFrame = true;
      this.#positionOf(sample, this.#pointer.releasePosition);
    }
  }

  #toWorld(camera: Camera, screen: Vector2, out: Vector2): void {
    camera.screenToWorld(screen.x, screen.y, out);
  }

  #applyKey(sample: KeySample): void {
    if (sample.phase === KeyPhase.Down) {
      if (!this.#keysDown.has(sample.code)) {
        this.#keysPressed.add(sample.code);
      }
      this.#keysDown.add(sample.code);
      return;
    }

    this.#keysDown.delete(sample.code);
    this.#keysReleased.add(sample.code);
  }
}
